// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  ReturnOrder,
  UpdateReturnOrderRequest,
  reasonToAPI,
  returnOrderSchema,
} from '@/return-order/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { z } from 'zod';

/**
 * Schema for validating the response from the update return order API endpoint.
 */
const updateReturnOrderResponseSchema = z.object({
  data: returnOrderSchema,
  metadata: responseMetadataSchema,
});

/**
 * Updates an existing return order with the specified details.
 *
 * @param returnOrderKey - The key of the return order to update
 * @param request - The return order details to update
 * @returns The updated return order
 */
export async function updateReturnOrder(
  returnOrderKey: string,
  request: UpdateReturnOrderRequest
): Promise<ReturnOrder> {
  const url = `/v1/return-order/${returnOrderKey}`;

  // Convert type to API format (uppercase) if provided
  const apiRequest = {
    ...request,
    returnType: request.returnType
      ? reasonToAPI(request.returnType)
      : undefined,
    promiseShipDate: serializeDateForAPI(request.promiseShipDate),
    promiseDeliveryDate: serializeDateForAPI(request.promiseDeliveryDate),
  };

  const response = await api.patch(url, apiRequest);

  const { data } = await parseResponse(
    updateReturnOrderResponseSchema,
    response.data,
    {
      schemaName: 'UpdateReturnOrder',
      endpoint: url,
    }
  );
  return data;
}
